import auth0 from 'auth0-js';

import { LOGIN_URL, LOGOUT_URL } from 'constants/urls';
import { backendURL, clearCache } from 'services/backendAPI';
import { DEMO_USER_EMAIL, DEMO_USER_PASSWORD } from 'constants/emails';
import { loginAPIUser } from 'services/resources/auth';
import { LOGIN_ERROR_MESSAGE } from 'constants/errorMessages';

const {
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_REDIRECT_URI,
  REACT_APP_NETLIFY_CALLBACK_URL,
  REACT_APP_LOGOUT_REDIRECT_URI,
} = process.env;

const auth0RedirectUri =
  REACT_APP_AUTH0_REDIRECT_URI || REACT_APP_NETLIFY_CALLBACK_URL;

// Auth0 can only hand the browser back to a real URL. The deployed builds get one from
// Netlify; a local .env carries a bare domain (or nothing), and sending the browser to
// Auth0 from there strands it on auth0.com with nowhere to return to.
export const isAuth0Configured = /^https?:\/\//.test(auth0RedirectUri || '');

const logoutCallbackUrl =
  REACT_APP_LOGOUT_REDIRECT_URI ||
  `${REACT_APP_NETLIFY_CALLBACK_URL}${LOGIN_URL}`;

const webAuth = new auth0.WebAuth({
  domain: REACT_APP_AUTH0_DOMAIN,
  clientID: REACT_APP_AUTH0_CLIENT_ID,
  redirectUri: auth0RedirectUri,
  responseType: 'token id_token',
});

// Why a login failed, in words that name the fix. A request that never got a response
// is almost always REACT_APP_API_URL pointing at a backend that is not running, which
// otherwise looks exactly like wrong credentials.
export const getLoginErrorMessage = (error) => {
  if (!error?.response) {
    return `Cannot reach the API at ${backendURL}. Check REACT_APP_API_URL in .env / .env.local.`;
  }
  const { data } = error.response;
  if (data && typeof data === 'object') {
    const messages = Object.values(data).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  return LOGIN_ERROR_MESSAGE;
};

export const loginWithAuth0 = async (email, password) =>
  new Promise((resolve, reject) => {
    webAuth.login(
      {
        email,
        password,
        realm: 'HistoWiz-Backend',
      },
      (error, result) => {
        if (error) {
          reject(error);

          return;
        }
        resolve(result);
      },
    );
  });

export const logout = (isAuthenticated, history) => {
  if (isAuthenticated) {
    webAuth.logout({
      returnTo: logoutCallbackUrl,
    });
  } else {
    history.push(LOGOUT_URL);
  }
  clearCache();
};

export const submitDemoLogin = ({
  updateStaff,
  getUserDetails,
  setRedirectToReferrer,
  showError,
}) => {
  const postParams = {
    email: DEMO_USER_EMAIL,
    password: DEMO_USER_PASSWORD,
  };

  loginAPIUser(postParams, updateStaff)
    .then(() => {
      getUserDetails();
      setRedirectToReferrer(true);
    })
    .catch((error) => {
      const message = getLoginErrorMessage(error);
      // eslint-disable-next-line no-console
      console.log(message);
      if (showError) showError(message);
    });
};

export const forceLogout = (isBasicRoute) => {
  const returnTo = isBasicRoute ? window.location.origin : logoutCallbackUrl;
  if (!isAuth0Configured) {
    // No Auth0 session to end locally, and its logout would bounce the browser to
    // "undefined/login/". Stay in the app.
    window.location.assign(isBasicRoute ? window.location.origin : LOGIN_URL);

    return;
  }
  webAuth.logout({ returnTo });
};

export const EMAIL_VALIDATION_REGULAR =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isEmailValid = (email) => EMAIL_VALIDATION_REGULAR.test(email);
